import React from 'react';

import styled from 'styled-components';

import { useIntl, FormattedMessage } from '@techstyle/react-intl';

import { mobile } from '../../styles';
import { getMeasurementLimitsByUnit, Unit } from '../../utils/braCalculator';

const Paragraph = styled.p`
  margin: ${({ theme }) => theme.spacing.small}px
    ${({ theme }) => theme.spacing.none}px;
`;

const MeasurementOptions = styled.div`
  display: flex;
  justify-content: flex-start;
  gap: ${({ theme }) => theme.spacing.small}px;
  margin-bottom: ${({ theme }) => theme.spacing.medium}px;
`;

const RadioButtonWrapper = styled.label`
  display: flex;
  align-items: center;
`;

const StyledRadioButton = styled.input.attrs({ type: 'radio' })`
  width: 20px;
  height: 20px;
  margin: ${({ theme }) => theme.spacing.none}px;
  margin-right: ${({ theme }) => theme.spacing.tiny}px;
  cursor: pointer;
  background: transparent;
  accent-color: ${({ theme }) => theme.colors.black};
`;

const InputWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${({ theme }) => theme.spacing.moderate}px;
  margin-bottom: ${({ theme }) => theme.spacing.medium}px;

  ${mobile`
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing.small}px;
    margin-bottom: ${({ theme }) => theme.spacing.small}px;
  `}
`;

const ValidatedInput = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const Input = styled.input<{ hasError: boolean }>`
  ${({ theme }) => theme.inputV2.defaultStyle}
  ${({ hasError, theme }) =>
    hasError
      ? `border: 1px solid ${theme.colors.red500};`
      : `border: 1px solid ${theme.colors.black};`}
  padding: ${({ theme }) => theme.spacing.small}px;
  line-height: 18px;

  ${mobile`
    flex: 1 1 100%;
  `}
`;

const InputError = styled.p`
  ${({ theme }) => theme.paragraph.variants.paragraph4.textStyles}
  color: ${({ theme }) => theme.colors.red500};
  margin-top: ${({ theme }) => theme.spacing.tiny}px;
`;

const FindMySizeButton = styled.button<{ showSizeResult: boolean }>`
  ${({ showSizeResult, theme }) =>
    showSizeResult
      ? theme.button.variants.secondaryDark.buttonStyle
      : theme.button.defaultStyle}
  width: 100%;
`;

interface BraCalculatorFormProps {
  unit: Unit;
  underbust: string;
  overbust: string;
  hasUnderbustError: boolean;
  hasOverbustError: boolean;
  showSizeResult: boolean;
  handleUnitChange: (unit: Unit) => void;
  handleUnderbustChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleOverbustChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleFindMySize: () => void;
}

const BraCalculatorForm: React.FC<BraCalculatorFormProps> = ({
  unit,
  underbust,
  overbust,
  hasUnderbustError,
  hasOverbustError,
  showSizeResult,
  handleUnitChange,
  handleUnderbustChange,
  handleOverbustChange,
  handleFindMySize,
}) => {
  const intl = useIntl();
  const { underbustMin, underbustMax, overbustMin, overbustMax } =
    getMeasurementLimitsByUnit(unit);

  return (
    <>
      <Paragraph>
        <FormattedMessage
          id="fit_guide.enter_measurements"
          defaultMessage={
            'Enter your measurements below to find your perfect fit—' +
            'no guesswork needed.'
          }
        />
      </Paragraph>
      <MeasurementOptions>
        <RadioButtonWrapper>
          <StyledRadioButton
            name="unit"
            data-autotag="inchesRadioButton"
            value="inches"
            checked={unit === 'inches'}
            onChange={() => handleUnitChange('inches')}
          />
          <FormattedMessage
            id="fit_guide.unit_inches"
            defaultMessage="Inches"
          />
        </RadioButtonWrapper>
        <RadioButtonWrapper>
          <StyledRadioButton
            name="unit"
            data-autotag="centimetersRadioButton"
            value="centimeters"
            checked={unit === 'centimeters'}
            onChange={() => handleUnitChange('centimeters')}
          />
          <FormattedMessage
            id="fit_guide.unit_centimeters"
            defaultMessage="Centimeters"
          />
        </RadioButtonWrapper>
      </MeasurementOptions>
      <InputWrapper>
        <ValidatedInput>
          <Input
            type="text"
            data-autotag="underbustInput"
            placeholder={
              unit === 'inches'
                ? intl.formatMessage(
                    {
                      id: 'fit_guide.underbust_from_to',
                      defaultMessage: 'Underbust (from {min}" to {max}")',
                    },
                    { min: underbustMin, max: underbustMax }
                  )
                : intl.formatMessage(
                    {
                      id: 'fit_guide.underbust_from_to_cms',
                      defaultMessage: 'Underbust (from {min} to {max} cms)',
                    },
                    { min: underbustMin, max: underbustMax }
                  )
            }
            value={underbust}
            onChange={handleUnderbustChange}
            hasError={hasUnderbustError}
          />
          {hasUnderbustError && (
            <InputError>
              <FormattedMessage
                id="fit_guide.invalid_input"
                defaultMessage="Invalid input, only numbers allowed."
              />
            </InputError>
          )}
        </ValidatedInput>
        <ValidatedInput>
          <Input
            type="text"
            data-autotag="overbustInput"
            placeholder={
              unit === 'inches'
                ? intl.formatMessage(
                    {
                      id: 'fit_guide.overbust_from_to',
                      defaultMessage: 'Overbust (from {min}" to {max}")',
                    },
                    { min: overbustMin, max: overbustMax }
                  )
                : intl.formatMessage(
                    {
                      id: 'fit_guide.overbust_from_to_cms',
                      defaultMessage: 'Overbust (from {min} to {max} cms)',
                    },
                    { min: overbustMin, max: overbustMax }
                  )
            }
            value={overbust}
            onChange={handleOverbustChange}
            hasError={hasOverbustError}
          />
          {hasOverbustError && (
            <InputError>
              <FormattedMessage
                id="fit_guide.invalid_input"
                defaultMessage="Invalid input, only numbers allowed."
              />
            </InputError>
          )}
        </ValidatedInput>
      </InputWrapper>
      <FindMySizeButton
        type="button"
        disabled={
          hasOverbustError || hasUnderbustError || !underbust || !overbust
        }
        data-autotag="findMySizeButton"
        showSizeResult={showSizeResult}
        onClick={handleFindMySize}
      >
        <FormattedMessage
          id="fit_guide.find_my_size"
          defaultMessage="Find My Size"
        />
      </FindMySizeButton>
    </>
  );
};

export default BraCalculatorForm;
